import React, { useState } from 'react';
import * as S from './styled';
import api from '../../services/api';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';

function ModalCadastro({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [termsAccepted, setTermsAccepted] = useState({
    privacy: false,
    terms: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Aplicar formatação automática
    if (name === 'phone') {
      formattedValue = formatarTelefone(value);
    } else if (name === 'cpf') {
      formattedValue = formatarCPF(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Validação em tempo real
    if (name === 'email' && value.trim()) {
      if (!validarEmail(value)) {
        setErrors(prev => ({ ...prev, email: 'Email inválido' }));
      }
    }
    
    if (name === 'phone' && value.trim()) {
      if (!validarTelefone(value)) {
        setErrors(prev => ({ ...prev, phone: 'Telefone deve ter 10 ou 11 dígitos' }));
      }
    }
    
    if (name === 'cpf' && value.trim()) {
      if (!validarCPF(value)) {
        setErrors(prev => ({ ...prev, cpf: 'CPF inválido' }));
      }
    }
    
    if (name === 'password') {
      // Atualizar requisitos da senha em tempo real
      setPasswordRequirements({
        length: value.length >= 6,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /[0-9]/.test(value),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(value)
      });
      
      if (value && !validarSenha(value)) {
        setErrors(prev => ({ ...prev, password: 'Senha deve atender todos os requisitos abaixo' }));
      }
    }
  };

  // Função para validar CPF
  const validarCPF = (cpf) => {
    return cpfValidator.isValid(cpf);
  };

  // Função para validar email
  const validarEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Função para validar telefone
  const validarTelefone = (telefone) => {
    const numeroLimpo = telefone.replace(/[^\d]/g, '');
    return numeroLimpo.length >= 10 && numeroLimpo.length <= 11;
  };

  // Função para validar senha
  const validarSenha = (senha) => {
    if (senha.length < 6) return false;
    
    const temMaiuscula = /[A-Z]/.test(senha);
    const temMinuscula = /[a-z]/.test(senha);
    const temNumero = /[0-9]/.test(senha);
    const temEspecial = /[!@#$%^&*(),.?":{}|<>]/.test(senha);
    
    return temMaiuscula && temMinuscula && temNumero && temEspecial;
  };

  // Função para formatar telefone
  const formatarTelefone = (value) => {
    const numero = value.replace(/\D/g, '');
    if (numero.length <= 10) {
      return numero.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return numero.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  };

  // Função para formatar CPF
  const formatarCPF = (value) => {
    const numero = value.replace(/\D/g, '');
    return numero.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Função para remover formatação do CPF
  const removerFormatacaoCPF = (cpf) => {
    return cpf.replace(/\D/g, '');
  };

  // Função para remover formatação do telefone
  const removerFormatacaoTelefone = (telefone) => {
    return telefone.replace(/\D/g, '');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!validarEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefone é obrigatório';
    } else if (!validarTelefone(formData.phone)) {
      newErrors.phone = 'Telefone deve ter 10 ou 11 dígitos';
    }

    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!validarCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (!validarSenha(formData.password)) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres, incluindo maiúscula, minúscula e caractere especial';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }

    if (!termsAccepted.privacy) {
      newErrors.privacy = 'Você deve aceitar a Política de Privacidade';
    }

    if (!termsAccepted.terms) {
      newErrors.terms = 'Você deve aceitar os Termos de Uso';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/create_user/plan', {
        name: formData.name,
        email: formData.email,
        phone: removerFormatacaoTelefone(formData.phone),
        cpf: removerFormatacaoCPF(formData.cpf),
        password: formData.password,
        plan: 'TESTE',
        termo_aceito: termsAccepted.privacy && termsAccepted.terms
      });

      console.log('✅ Usuário criado com sucesso:', response.data);
      setSuccess(true);
      
      // Resetar formulário após 2 segundos e fechar modal
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          cpf: '',
          password: '',
          confirmPassword: ''
        });
        setTermsAccepted({
          privacy: false,
          terms: false
        });
        setSuccess(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Erro ao criar usuário:', error);
      
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else {
        setErrors({ submit: 'Erro interno do servidor. Tente novamente.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  const formatPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4,5})(\d{4})/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setFormData(prev => ({ ...prev, cpf: formatted }));
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const getPlanTitle = () => {
    return 'Experimentação Gratuita';
  };

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>{getPlanTitle()}</S.ModalTitle>
          <S.CloseButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </S.CloseButton>
        </S.ModalHeader>

        {success ? (
          <S.SuccessMessage>
            <S.SuccessIcon>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.41,10.09L6,11.5L11,16.5Z"/>
              </svg>
            </S.SuccessIcon>
            <h3>Cadastro realizado com sucesso!</h3>
            <p>Bem-vindo(a) ao Curso Danielle Velasco!</p>
          </S.SuccessMessage>
        ) : (
          <S.ModalForm onSubmit={handleSubmit}>
            <S.FormRow>
              <S.FormGroup>
                <S.Label>Nome Completo *</S.Label>
                <S.Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Digite seu nome completo"
                  hasError={!!errors.name}
                />
                {errors.name && <S.ErrorMessage>{errors.name}</S.ErrorMessage>}
              </S.FormGroup>
            </S.FormRow>

            <S.FormRow>
              <S.FormGroup>
                <S.Label>Email *</S.Label>
                <S.Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="exemplo@email.com"
                  hasError={!!errors.email}
                />
                {errors.email && <S.ErrorMessage>{errors.email}</S.ErrorMessage>}
              </S.FormGroup>
            </S.FormRow>

            <S.FormRow>
              <S.FormGroup>
                <S.Label>Telefone *</S.Label>
                <S.Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  hasError={!!errors.phone}
                  maxLength="15"
                />
                {errors.phone && <S.ErrorMessage>{errors.phone}</S.ErrorMessage>}
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>CPF *</S.Label>
                <S.Input
                  type="text"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleCPFChange}
                  placeholder="000.000.000-00"
                  hasError={!!errors.cpf}
                  maxLength="14"
                />
                {errors.cpf && <S.ErrorMessage>{errors.cpf}</S.ErrorMessage>}
              </S.FormGroup>
            </S.FormRow>

            <S.FormRow>
              <S.FormGroup>
                <S.Label>Senha *</S.Label>
                <S.Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Mínimo 6 caracteres (A-z, 0-9, !@#)"
                  hasError={!!errors.password}
                />
                
                {/* Indicadores de requisitos da senha */}
                <S.PasswordRequirements>
                  <S.RequirementItem isValid={passwordRequirements.length}>
                    <S.RequirementIcon isValid={passwordRequirements.length}>
                      {passwordRequirements.length ? '✓' : '✗'}
                    </S.RequirementIcon>
                    Pelo menos 6 caracteres
                  </S.RequirementItem>
                  
                  <S.RequirementItem isValid={passwordRequirements.uppercase}>
                    <S.RequirementIcon isValid={passwordRequirements.uppercase}>
                      {passwordRequirements.uppercase ? '✓' : '✗'}
                    </S.RequirementIcon>
                    Pelo menos 1 letra maiúscula
                  </S.RequirementItem>
                  
                  <S.RequirementItem isValid={passwordRequirements.lowercase}>
                    <S.RequirementIcon isValid={passwordRequirements.lowercase}>
                      {passwordRequirements.lowercase ? '✓' : '✗'}
                    </S.RequirementIcon>
                    Pelo menos 1 letra minúscula
                  </S.RequirementItem>
                  
                  <S.RequirementItem isValid={passwordRequirements.number}>
                    <S.RequirementIcon isValid={passwordRequirements.number}>
                      {passwordRequirements.number ? '✓' : '✗'}
                    </S.RequirementIcon>
                    Pelo menos 1 número
                  </S.RequirementItem>
                  
                  <S.RequirementItem isValid={passwordRequirements.special}>
                    <S.RequirementIcon isValid={passwordRequirements.special}>
                      {passwordRequirements.special ? '✓' : '✗'}
                    </S.RequirementIcon>
                    Pelo menos 1 caractere especial (@, #, etc.)
                  </S.RequirementItem>
                </S.PasswordRequirements>
                
                {errors.password && <S.ErrorMessage>{errors.password}</S.ErrorMessage>}
              </S.FormGroup>

              <S.FormGroup>
                <S.Label>Confirmar Senha *</S.Label>
                <S.Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirme sua senha"
                  hasError={!!errors.confirmPassword}
                />
                {errors.confirmPassword && <S.ErrorMessage>{errors.confirmPassword}</S.ErrorMessage>}
              </S.FormGroup>
            </S.FormRow>

            <S.FormRow>
              <S.CheckboxGroup>
                <S.CheckboxContainer>
                  <S.Checkbox
                    type="checkbox"
                    id="privacy"
                    checked={termsAccepted.privacy}
                    onChange={(e) => {
                      setTermsAccepted(prev => ({ ...prev, privacy: e.target.checked }));
                      if (errors.privacy) {
                        setErrors(prev => ({ ...prev, privacy: '' }));
                      }
                    }}
                  />
                  <S.CheckboxLabel htmlFor="privacy">
                    Li e aceito a{' '}
                    <S.CheckboxLink
                      href="./Politica de Privacidade.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Política de Privacidade
                    </S.CheckboxLink>
                    {' '}*
                  </S.CheckboxLabel>
                </S.CheckboxContainer>
                {errors.privacy && <S.ErrorMessage>{errors.privacy}</S.ErrorMessage>}
              </S.CheckboxGroup>
            </S.FormRow>

            <S.FormRow>
              <S.CheckboxGroup>
                <S.CheckboxContainer>
                  <S.Checkbox
                    type="checkbox"
                    id="terms"
                    checked={termsAccepted.terms}
                    onChange={(e) => {
                      setTermsAccepted(prev => ({ ...prev, terms: e.target.checked }));
                      if (errors.terms) {
                        setErrors(prev => ({ ...prev, terms: '' }));
                      }
                    }}
                  />
                  <S.CheckboxLabel htmlFor="terms">
                    Li e aceito os{' '}
                    <S.CheckboxLink
                      href="./Termos de Uso de serviço e condições de uso.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Termos de Uso e Condições
                    </S.CheckboxLink>
                    {' '}*
                  </S.CheckboxLabel>
                </S.CheckboxContainer>
                {errors.terms && <S.ErrorMessage>{errors.terms}</S.ErrorMessage>}
              </S.CheckboxGroup>
            </S.FormRow>

            {errors.submit && (
              <S.ErrorMessage style={{ textAlign: 'center', marginBottom: '1rem' }}>
                {errors.submit}
              </S.ErrorMessage>
            )}

            <S.SubmitButton 
              type="submit" 
              disabled={loading || !termsAccepted.privacy || !termsAccepted.terms}
            >
              {loading ? (
                <>
                  <S.LoadingSpinner />
                  Criando conta...
                </>
              ) : (
                `Criar conta - ${getPlanTitle()}`
              )}
            </S.SubmitButton>

            <S.LoginLink>
              Já tem uma conta? <a href="/login">Faça login aqui</a>
            </S.LoginLink>
          </S.ModalForm>
        )}
      </S.ModalContent>
    </S.ModalOverlay>
  );
}

export default ModalCadastro;